import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { useCartStore } from '@/store/cartStore';
import { useEmpresaStore } from '@/store/empresaStore';
import { useAuthStore } from '@/store/authStore';
import { clientesService } from '@/api/clientes';
import { pedidosService } from '@/api/pedidos';
import type { CreatePedidoRequest } from '@/types';
import { ShoppingCart, Store, Plus, Minus } from 'lucide-react';
import AppHeader from '@/components/layout/AppHeader';
import LoadingSpinner from '@/components/common/LoadingSpinner';

export default function CarritoPage() {
  const navigate = useNavigate();
  const { clienteId, items, updateQuantity, removeItem, clearCart } = useCartStore();
  const empresaSeleccionada = useEmpresaStore((state) => state.empresaSeleccionada);
  const user = useAuthStore((state) => state.user);
  const [selectedDireccionIndex, setSelectedDireccionIndex] = useState<number>(0);
  const [observaciones, setObservaciones] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const { data: cliente } = useQuery({
    queryKey: ['cliente-en-carrito', clienteId],
    queryFn: () => (clienteId ? clientesService.getClienteById(clienteId) : Promise.resolve(null)),
    enabled: !!clienteId,
  });

  const { data: direcciones, isLoading: loadingDirecciones } = useQuery({
    queryKey: ['direcciones', clienteId],
    queryFn: () => (clienteId ? clientesService.getDirecciones(clienteId) : Promise.resolve([])),
    enabled: !!clienteId,
  });

  const createPedidoMutation = useMutation({
    mutationFn: (data: CreatePedidoRequest) => pedidosService.createPedido(data),
    onSuccess: () => {
      setShowSuccess(true);
      setTimeout(() => {
        clearCart();
        navigate('/clientes', {
          state: { message: 'Pedido creado correctamente' },
        });
      }, 2000);
    },
    onError: (err: unknown) => {
      if (axios.isAxiosError(err)) {
        console.error('Response data:', err.response?.data);
        console.error('Response status:', err.response?.status);
      }
      const message =
        axios.isAxiosError(err) && err.response?.data?.message
          ? err.response.data.message
          : axios.isAxiosError(err) && err.response?.data
            ? JSON.stringify(err.response.data)
            : err instanceof Error
              ? err.message
              : 'Error al crear el pedido';
      setError(message);
    },
  });

  useEffect(() => {
    if (!showSuccess) return;

    const duration = 2000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.6 },
        colors: ['#27648d', '#3B82F6', '#10B981', '#F59E0B'],
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.6 },
        colors: ['#27648d', '#3B82F6', '#10B981', '#F59E0B'],
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    frame();
  }, [showSuccess]);

  useEffect(() => {
    if (!direcciones || direcciones.length === 0) return;

    const defaultIndex = direcciones.findIndex((direccion) => direccion.esPredeterminado);
    setSelectedDireccionIndex(defaultIndex >= 0 ? defaultIndex : 0);
  }, [direcciones]);

  const handleCrearPedido = () => {
    if (!clienteId) {
      setError('No hay cliente seleccionado');
      return;
    }

    if (!empresaSeleccionada) {
      setError('No hay empresa seleccionada');
      return;
    }

    if (!user) {
      setError('Usuario no autenticado');
      return;
    }

    if (items.length === 0) {
      setError('El carrito esta vacio');
      return;
    }

    if (!direcciones || direcciones.length === 0) {
      setError('No hay direcciones de entrega disponibles');
      return;
    }

    const selectedDireccion = direcciones[selectedDireccionIndex];
    if (!selectedDireccion) {
      setError('Debes seleccionar una direccion de entrega');
      return;
    }

    const numeroDomicilioRaw = selectedDireccion.numeroDomicilio || selectedDireccionIndex + 1;
    const numeroDomicilio = String(numeroDomicilioRaw);
    const fechaActual = new Date().toISOString();

    const lineas = items.map((item) => ({
      codigoEmpresa: empresaSeleccionada.id,
      codigoArticulo: String(item.articulo.codigo || ''),
      unidades: item.cantidad,
      precio: item.articulo.precio,
      porcentajeDescuento: item.descuento,
      descripcionArticulo: item.articulo.nombre,
      peso: item.articulo.peso || 0,
      unidadMedida1: item.articulo.unidadMedida || '',
    }));

    const pedidoData: CreatePedidoRequest = {
      cabecera: {
        codigoEmpresa: empresaSeleccionada.id,
        codigoCliente: clienteId,
        codigoComisionista: '0',
        fechaPedido: fechaActual,
        numeroDomicilio,
        statusProcesado: 0,
        observaciones: observaciones.trim() || undefined,
        ogFechaSalida: null,
      },
      lineas,
    };

    setError(null);
    createPedidoMutation.mutate(pedidoData);
  };

  const renderMessageScreen = (
    title: string,
    description: string,
    buttonLabel: string,
    action: () => void
  ) => (
    <div className="min-h-screen bg-[#f5f5f5]">
      <AppHeader title="Configuracion del Pedido" subtitle="Revisa cantidades, descuentos y lineas" />
      <main className="mx-auto flex w-full max-w-[1300px] flex-col items-center justify-center px-4 py-12 sm:px-6">
        <div className="w-full max-w-xl rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-3xl bg-slate-100">
            <ShoppingCart className="h-10 w-10 text-slate-400" />
          </div>
          <h3 className="mb-2 text-xl font-bold text-[#0d141b]">{title}</h3>
          <p className="mb-6 text-[#4c739a]">{description}</p>
          <button
            onClick={action}
            className="w-full rounded-xl bg-[#27648d] py-3 font-bold text-white shadow-md transition-colors hover:bg-[#1f5479]"
            type="button"
          >
            {buttonLabel}
          </button>
        </div>
      </main>
    </div>
  );

  if (!clienteId) {
    return renderMessageScreen(
      'No hay cliente seleccionado',
      'Selecciona un cliente antes de continuar con el pedido.',
      'Seleccionar Cliente',
      () => navigate('/clientes')
    );
  }

  if (items.length === 0) {
    return renderMessageScreen(
      'Carrito vacio',
      'Agrega articulos desde el catalogo para configurar un pedido.',
      'Ir al Catalogo',
      () => navigate('/catalogo')
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5f5] pb-32">
      <AppHeader title="Configuracion del Pedido" subtitle="Revisa cantidades, descuentos y lineas" />

      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-3xl bg-white p-8 shadow-2xl">
            <div className="flex flex-col items-center gap-4">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                <span className="material-symbols-outlined text-5xl text-green-600">check_circle</span>
              </div>
              <h3 className="text-2xl font-bold text-slate-900">Pedido Creado</h3>
              <p className="text-center text-slate-500">
                Tu pedido ha sido registrado correctamente y ya se esta procesando.
              </p>
              <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-slate-200">
                <div className="h-full w-full animate-pulse bg-[#27648d]" />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mx-auto w-full max-w-[1300px] px-4 pb-32 pt-6 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white">
          <div className="border-b border-slate-200 p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <h2 className="mb-2 text-2xl font-bold text-[#0d141b]">
                  {cliente?.nombre ?? 'Cliente seleccionado'}
                </h2>
                <div className="mb-1 flex items-center gap-2 text-sm text-[#4c739a]">
                  <Store className="h-4 w-4" />
                  <span>{cliente?.tarifaNombre ?? 'Tarifa B2B (Premium)'}</span>
                </div>
                {cliente?.ciudad && <p className="text-sm text-[#4c739a]">{cliente.ciudad}</p>}
              </div>
              <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-[#27648d]">
                <Store className="h-7 w-7 text-white" />
              </div>
            </div>
          </div>

          <section className="p-4">
            <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="flex items-center gap-2 font-bold text-[#0d141b]">
                <span className="material-symbols-outlined">inventory_2</span>
                Articulos en el pedido
              </h3>
              <span className="inline-flex w-fit rounded-full bg-sky-100 px-3 py-1 text-xs font-medium text-sky-800">
                {items.length} productos
              </span>
            </div>

            <div className="space-y-3">
              {items.map((item) => (
                <div key={item.articulo.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="flex-1">
                      <p className="text-base font-semibold text-[#0d141b]">{item.articulo.nombre}</p>
                    </div>

                    <button
                      onClick={() => removeItem(item.articulo.id)}
                      className="inline-flex items-center gap-1 rounded-lg bg-red-500 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-red-600"
                      type="button"
                    >
                      Eliminar
                    </button>
                  </div>

                  <div className="mt-3 flex items-center gap-3">
                    <span className="text-sm text-[#4c739a]">Cantidad:</span>
                    <div className="flex items-center gap-2 rounded-lg bg-slate-100 p-1">
                      <button
                        onClick={() => {
                          if (item.cantidad === 1) {
                            removeItem(item.articulo.id);
                          } else {
                            updateQuantity(item.articulo.id, item.cantidad - 1);
                          }
                        }}
                        className="flex h-8 w-8 items-center justify-center rounded-lg bg-white transition-colors hover:bg-slate-200"
                        type="button"
                      >
                        <Minus className="h-4 w-4 text-slate-700" />
                      </button>
                      <input
                        className="w-12 border-none bg-transparent text-center text-sm font-bold text-[#0d141b] focus:outline-none"
                        type="number"
                        value={item.cantidad}
                        onChange={(e) => updateQuantity(item.articulo.id, parseInt(e.target.value, 10) || 1)}
                        min={1}
                      />
                      <button
                        onClick={() => updateQuantity(item.articulo.id, item.cantidad + 1)}
                        className="flex h-8 w-8 items-center justify-center rounded-lg bg-white transition-colors hover:bg-slate-200"
                        type="button"
                      >
                        <Plus className="h-4 w-4 text-slate-700" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <section className="mt-6 rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
          <div className="mb-4">
            <h2 className="text-lg font-bold tracking-tight text-slate-900">Direccion de Entrega</h2>
            <p className="text-sm text-slate-500">Selecciona el domicilio donde se recibira el pedido.</p>
          </div>

          {loadingDirecciones ? (
            <div className="py-10">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="grid gap-3">
              {direcciones?.map((direccion, index) => (
                <label
                  key={`direccion-${index}`}
                  className={`cursor-pointer rounded-2xl border p-4 transition-colors ${
                    selectedDireccionIndex === index
                      ? 'border-sky-500 bg-sky-50 ring-2 ring-sky-200'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="delivery-address"
                      checked={selectedDireccionIndex === index}
                      onChange={() => setSelectedDireccionIndex(index)}
                      className="mt-1 h-5 w-5 border-slate-300 text-sky-600 focus:ring-sky-500"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <p className="text-base font-semibold text-slate-900">{direccion.direccion}</p>
                        {direccion.esPredeterminado && (
                          <span className="rounded-full bg-sky-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                            Predeterminado
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">
                        {direccion.ciudad}, CP {direccion.codigoPostal}
                      </p>
                      {direccion.numeroDomicilio && (
                        <p className="mt-1 text-xs text-slate-500">N.o Domicilio: {direccion.numeroDomicilio}</p>
                      )}
                    </div>
                  </div>
                </label>
              ))}

              {(!direcciones || direcciones.length === 0) && (
                <div className="rounded-2xl border border-dashed border-slate-300 px-6 py-10 text-center">
                  <p className="text-slate-500">No hay direcciones registradas.</p>
                </div>
              )}
            </div>
          )}
        </section>

        <section className="mt-6 rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
            <label className="flex flex-col gap-3">
              <div>
                <h2 className="text-lg font-bold tracking-tight text-slate-900">Observaciones de la Entrega</h2>
                <p className="text-sm text-slate-500">
                  Indica horarios, referencias o cualquier instruccion especial.
                </p>
              </div>
              <textarea
                className="min-h-[180px] w-full resize-y rounded-2xl border border-slate-200 bg-slate-50 p-4 text-base text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                placeholder="Ingrese instrucciones especiales para la entrega, horarios preferidos o referencias adicionales..."
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
              />
            </label>
        </section>

        {error && (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>

      <footer className="fixed bottom-0 left-0 right-0 z-10 border-t border-slate-200 bg-white shadow-lg">
        <div className="mx-auto flex w-full max-w-[1300px] justify-center px-4 py-4 sm:px-6 lg:px-8">
          <button
            onClick={handleCrearPedido}
            className="w-full rounded-xl bg-[#27648d] py-4 text-lg font-bold text-white shadow-md transition-colors hover:bg-[#1f5479] lg:max-w-sm disabled:cursor-not-allowed disabled:opacity-70"
            type="button"
            disabled={createPedidoMutation.isPending || loadingDirecciones}
          >
            {createPedidoMutation.isPending ? 'Creando Pedido...' : 'Finalizar Pedido'}
          </button>
        </div>
      </footer>
    </div>
  );
}
